/**
 * Assertions (W1-01 core-time-math): `assertNever` for exhaustive switches, `assert` / `invariant` for internal
 * consistency checks and `defined` for values that the surrounding logic guarantees.
 *
 * Messages are developer diagnostics (English, never shown as UI text); user-facing failures are MessageRefs.
 * A lazily built message (`() => string`) costs nothing on the success path.
 */

import type { AssertNever } from './types';

/** Thrown by every helper in this module. */
export class AssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssertionError';
  }
}

type Msg = string | (() => string);

function text(msg: Msg | undefined, fallback: string): string {
  if (msg === undefined) return fallback;
  return typeof msg === 'function' ? msg() : msg;
}

/** Short, safe description of an unexpected value for diagnostics. */
export function describeValue(x: unknown): string {
  if (typeof x === 'string') return JSON.stringify(x);
  if (typeof x === 'bigint') return `${x.toString()}n`;
  if (typeof x === 'function') return '[function]';
  if (typeof x === 'symbol') return x.toString();
  if (x === null || typeof x !== 'object') return String(x);
  try {
    const s = JSON.stringify(x);
    return s.length > 200 ? `${s.slice(0, 200)}...` : s;
  } catch {
    return Object.prototype.toString.call(x);
  }
}

/** Exhaustiveness check: `default: return assertNever(x)` fails to compile when a case is missing. */
export const assertNever: AssertNever = (x, msg) => {
  throw new AssertionError(msg ?? `Unexpected value: ${describeValue(x)}`);
};

/** Throws an AssertionError unless `cond` is truthy. */
export function assert(cond: unknown, msg?: Msg): asserts cond {
  if (!cond) throw new AssertionError(text(msg, 'Assertion failed'));
}

/** Same as `assert`, named for model / store invariants. */
export function invariant(cond: unknown, msg?: Msg): asserts cond {
  if (!cond) throw new AssertionError(text(msg, 'Invariant violated'));
}

/** Returns x, throwing when it is null or undefined (e.g. after an index lookup the logic guarantees). */
export function defined<T>(x: T | null | undefined, msg?: Msg): T {
  if (x === null || x === undefined) throw new AssertionError(text(msg, 'Expected a defined value'));
  return x;
}

/** Marks code that must never run. */
export function unreachable(msg?: Msg): never {
  throw new AssertionError(text(msg, 'Unreachable code reached'));
}
